import { Formik } from 'formik'
import React from 'react'
import { useParams } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { HeadContent, SaveShortcutListener, Toasts } from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import { useCreateImageResources } from '../../hooks/useCreateImageResources'
import { useTagsQuery } from '../../hooks/useTagsQuery'
import { useTiers } from '../Perks/hooks/useTiers'
import { PostViewSkeleton } from './PostSkeleton'
import { PostLayout } from './components/PostLayout'
import { getValidationSchema } from './consts'
import { useFinishLiveStreamPostMutation } from './hooks/useFinishLiveStreamPostMutation'
import { useImageResources } from './hooks/useImageResources'
import { usePost } from './hooks/usePost'
import { usePostSeriesQuery } from './hooks/usePostSeriesQuery'
import { usePostUpdate } from './hooks/usePostUpdate'
import { useResourceLiveStreamQuery } from './hooks/useResourceLiveStreamQuery'
import { useStartLiveStreamPostMutation } from './hooks/useStartLiveStreamPostMutation'
import { AudioFields } from './modules/AudioFields'
import { CommonFields } from './modules/CommonFields'
import { ImagesFields } from './modules/ImagesFields'
import { LiveStreamPreview } from './modules/LiveStreamPreview/LiveStreamPreview'
import { LiveStreamRecordingFields } from './modules/LiveStreamRecordingFields'
import { ReadOnlyPollFields } from './modules/ReadOnlyPollFields'
import { SeriesFields } from './modules/SeriesFields'
import { VideoFields } from './modules/VideoFields'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getSelectedTierIds } from './utils/getSelectedTierIds'

export const Post = () => {
  const { postId = '' } = useParams<'postId'>()
  const { post, postQuery } = usePost(postId)
  const { images, isLoading: imageResourcesIsLoading } = useImageResources(postId)
  const { tiers } = useTiers()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { tagsQuery } = useTagsQuery([{ limit: 9999 }])
  const { postSeriesQuery } = usePostSeriesQuery(
    Number(postId),
    post?.type === 'Video' || post?.type === 'VideoEmbed' || post?.type === 'LiveStream',
  )

  const { startLiveStreamPostMutation } = useStartLiveStreamPostMutation()
  const { finishLiveStreamPostMutation } = useFinishLiveStreamPostMutation()

  const { resourceLiveStreamQuery } = useResourceLiveStreamQuery(
    [{ id: post && post.type === 'LiveStream' ? post.streamResourceId : 1 }],
    {
      enabled: !!(post && post.type === 'LiveStream'),
      refetchInterval: (data) => {
        if (!data?.streamEndDate || !data.videoRecording) {
          return 5000
        }

        return false
      },
    },
  )

  const { updatePost, isLoading, mediaUploadProgress } = usePostUpdate(postId)

  const { createImageResources, isLoading: createImageResourcesIsLoading } =
    useCreateImageResources()

  const isErrored = post?.statuses.resources === 'Errored'
  const disabled =
    isLoading || imageResourcesIsLoading || isErrored || resourceLiveStreamQuery.isFetching

  if (!post || !tiers || !tagsQuery.data) {
    return <PostViewSkeleton />
  }

  if (post.type === 'LiveStream' && !resourceLiveStreamQuery.data) {
    return <PostViewSkeleton />
  }

  if (post.type === 'Video' && !postSeriesQuery.data) {
    return <PostViewSkeleton />
  }

  if (post.type === 'Image' && !images) {
    return <PostViewSkeleton />
  }

  const tiersIds = post.access.tiers?.map(({ id }) => id)
  const getTiers = getSelectedTierIds(tiersIds || [], tiers)

  const getTags = () =>
    post.tags.map((postTag) => tagsQuery.data.tags.find((tag) => tag.id === postTag.id)?.name || '')

  const getPollOptions = () => {
    if ('options' in post) {
      return post.options?.map((option) => option.title || '')
    }

    return []
  }

  const getImageUrl = () => {
    if ('image' in post && post.image && 'url' in post.image) {
      return post.image.url
    }
  }

  const initialValues: FormValues = {
    mode: 'update',
    liveAt: new Date(post.liveAt),
    postType: post.type,
    access: post.access.type,
    tierIds: getTiers,
    title: post.title,
    description: post.description,
    notifyMembers: post.notifications.type === 'Enabled',
    tags: getTags(),
    image: getImageUrl(),
    images: images || [],
    audio: post.type === 'Audio' ? post.mediaUrl : undefined,
    audioThumbnail: getImageUrl(),
    video: post.type === 'Video' ? post.mediaUrl : undefined,
    videoType: post.type === 'VideoEmbed' ? 'embed' : 'upload',
    videoThumbnail: getImageUrl(),
    videoSubtitles: post.type === 'Video' ? !!post.customSubtitles || undefined : undefined,
    videoUrl: post.type === 'VideoEmbed' ? post.embed.url : undefined,
    videoSeries:
      post.type === 'Video' || post.type === 'VideoEmbed' || post.type === 'LiveStream'
        ? postSeriesQuery.data?.series
        : [],
    pollOptions: getPollOptions(),
    liveStreamStartDate: resourceLiveStreamQuery.data?.streamStartDate || '',
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={getValidationSchema({
        pollLenghValidation: false,
        publicVideo: dashboardFeatureFlags.membershipsPublicVideo.enabled,
        scheduleStatus: post.statuses.schedule,
      })}
      enableReinitialize
      onSubmit={updatePost}
    >
      {({ values, dirty, isValid, setFieldValue, submitForm, resetForm }) => {
        const handleImagesUpload = (files: File[]) => {
          createImageResources({
            files,
            onSuccess: (images) =>
              setFieldValue(FormFields.images, [
                ...values.images,
                ...images.map((image) => ({ image: image.resizedUrl, id: image.id })),
              ]),
          })
        }

        return (
          <>
            <HeadContent title={post.title} />
            {post.type === 'Text' && (
              <PostLayout disabled={disabled}>
                <CommonFields disabled={disabled} />
              </PostLayout>
            )}
            {post.type === 'Image' && (
              <PostLayout disabled={disabled}>
                <ImagesFields
                  imagesFieldName={FormFields.images}
                  subsectionTitle="Images"
                  isUploading={createImageResourcesIsLoading}
                  isLoading={isLoading}
                  onImagesUpload={handleImagesUpload}
                />
                <CommonFields disabled={disabled} />
              </PostLayout>
            )}
            {(post.type === 'Video' || post.type === 'VideoEmbed') && (
              <PostLayout disabled={disabled}>
                {/* @ts-ignore */}
                <VideoFields
                  type={values.videoType}
                  typeFieldName={FormFields.videoType}
                  video={values.video}
                  videoFieldName={FormFields.video}
                  thumbnail={values.videoThumbnail}
                  thumbnailFieldName={FormFields.videoThumbnail}
                  subtitles={values.videoSubtitles}
                  subtitlesFieldName={FormFields.videoSubtitles}
                  customSubtitles={post.type === 'Video' ? post.customSubtitles : undefined}
                  isThumbnailCustom={post.image?.type === 'Custom'}
                  url={values.videoUrl}
                  urlFieldName={FormFields.videoUrl}
                  subsectionTitle="Video"
                  uploadProgress={mediaUploadProgress}
                  disabled={isErrored}
                  isUploading={
                    isLoading &&
                    (typeof values.video === 'object' ||
                      typeof values.videoThumbnail === 'object' ||
                      typeof values.videoSubtitles === 'object')
                  }
                  status={post.statuses.resources}
                  updatedAt={post.updatedAt}
                />
                <CommonFields disabled={disabled}>
                  <SeriesFields disabled={disabled} />
                </CommonFields>
              </PostLayout>
            )}
            {post.type === 'Audio' && (
              <PostLayout disabled={disabled}>
                <AudioFields
                  uploadProgress={mediaUploadProgress}
                  isUploading={
                    isLoading &&
                    (typeof values.audio === 'object' || typeof values.audioThumbnail === 'object')
                  }
                  status={post.statuses.resources}
                />
                <CommonFields disabled={disabled} />
              </PostLayout>
            )}
            {post.type === 'Poll' && (
              <PostLayout disabled={disabled}>
                <CommonFields disabled={disabled} />
                <ReadOnlyPollFields
                  pollFinishDate={post.finishedAt}
                  pollOptions={post.options}
                  votesAmount={post.votesAmount}
                />
              </PostLayout>
            )}
            {post.type === 'LiveStream' && (
              <PostLayout
                disabled={isLoading}
                liveStreamStartDate={resourceLiveStreamQuery.data?.streamStartDate || ''}
                liveStreamPreview={
                  !resourceLiveStreamQuery.data?.streamEndDate && (
                    <LiveStreamPreview
                      rtmpsUrl={resourceLiveStreamQuery.data?.rtmpsUrl || ''}
                      srtUrl={resourceLiveStreamQuery.data?.srtUrl || ''}
                      streamKey={resourceLiveStreamQuery.data?.streamKey || ''}
                      status={resourceLiveStreamQuery.data?.streamStatus || 'Idle'}
                      playbackId={resourceLiveStreamQuery.data?.playbackId || ''}
                      playbackToken={resourceLiveStreamQuery.data?.playbackToken || ''}
                      isLoading={
                        finishLiveStreamPostMutation.isLoading ||
                        startLiveStreamPostMutation.isLoading
                      }
                      onEndLiveStreamClick={() =>
                        finishLiveStreamPostMutation.mutate([{ id: post.id! }], {
                          onSuccess: () => {
                            resourceLiveStreamQuery.refetch()
                            Toasts.notify(
                              'Your recording is being processed. This may take a few minutes.',
                              { type: 'info' },
                            )
                          },
                        })
                      }
                      onStartLiveStreamClick={() =>
                        startLiveStreamPostMutation.mutate([{ id: post.id! }], {
                          onSuccess: resourceLiveStreamQuery.refetch,
                        })
                      }
                      onCountdownEnd={resourceLiveStreamQuery.refetch}
                    />
                  )
                }
              >
                <CommonFields disabled={isLoading}>
                  <SeriesFields disabled={disabled} />
                </CommonFields>
                <LiveStreamRecordingFields
                  streamHasEnded={!!resourceLiveStreamQuery.data?.streamEndDate}
                  playbackId={resourceLiveStreamQuery.data?.videoRecording?.playbackId || ''}
                  playbackToken={resourceLiveStreamQuery.data?.videoRecording?.playbackToken || ''}
                  rtmpsUrl={resourceLiveStreamQuery.data?.rtmpsUrl || ''}
                  srtUrl={resourceLiveStreamQuery.data?.srtUrl || ''}
                  streamKey={resourceLiveStreamQuery.data?.streamKey || ''}
                />
              </PostLayout>
            )}

            <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
            <ConfirmBar
              isOpen={dirty}
              isLoading={isLoading || createImageResourcesIsLoading || postQuery.isFetching}
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </>
        )
      }}
    </Formik>
  )
}
